import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import { CollaborativeEditor } from '../../../shared/ui/CollaborativeEditor/CollaborativeEditor.jsx';
import { TaskCommentMention, TaskCommentImage, TaskCommentVideo, TaskCommentFileAttachment } from './comment-editor/taskCommentNodes.js';
import { createTaskCommentSuggestion } from './comment-editor/taskCommentSuggestion.js';
import { uploadAndInsertFile } from '../lib/taskCommentUpload.js';
import LilMenu from '../../docs/components/document-text/lilmenu/lilmenu.jsx';
import TableMenu from '../../docs/components/document-text/tablemenu/TableMenu.jsx';
import { StickyTableHeadersOverlay } from '../../docs/components/document-text/tablemenu/StickyTableHeadersOverlay';
import { DatabaseExtension } from '../../docs/components/extensions/DatabaseExtension';
import { DocTagExtension } from '../../docs/components/extensions/DocTagExtension';
import { MentionExtension } from '../../docs/components/extensions/MentionExtension';
import { ColumnExtension, ColumnContainerExtension } from '../../docs/components/extensions/ColumnExtension';
import { InlineFrameExtension } from '../../docs/components/extensions/InlineFrameExtension';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { rgbToHex, hexToRgbStr } from '../../docs/lib/docLinkColor';
import './TaskDescriptionEditor.css';

// slice 02.1 · paso b.5b — presentacional. El link a la tarea padre + el editor colaborativo
// (Yjs) de la descripción + su barra de subida. Era JSX inline de `ComponentTaskHoy`
// (parte de `renderInfoContent`).
//
// `key`/`documentId` son load-bearing: la misma sala hocuspocus `task-desc-${taskId}` y la
// clase `.task-panel-description-textarea` las comparte `PublicTaskView.jsx` (vista pública
// `/t/:taskId`, logueado o no) -- de ahí el CSS propio (`TaskDescriptionEditor.css`) en vez de
// vivir en `ComponentTaskHoy.css`. No hay estado `initialDescHtml`: `initialHtml={initialHtml}`
// entra directo desde `activeTask.description`; el remount por `key` resetea el editor y
// `hasHydratedRef` (adentro de `CollaborativeEditor`) protege contra pisar cambios posteriores.
//
// Toda mutación/toast queda en el contenedor (boundary lint): `onSaveDescription` es la única
// escritura. El debounce de 1.2s y la barra de subida quedan acá (estado genuinamente local).
export const TaskDescriptionEditor = ({
    taskId,
    initialHtml,
    userId,
    currentUser,
    isDatePopoverOpen,
    onSaveDescription,
    parentTask,
    systemUsers = [],
    usersToSelectFrom = [],
    onOpenTask,
}) => {
    const { t: translate } = useTranslation('tasks');
    const [uploadProgress, setUploadProgress] = useState(0);
    const saveTimeoutRef = useRef(null);

    const [editor, setEditor] = useState(null);
    const [mentionAnchorEl, setMentionAnchorEl] = useState(null);
    const [lilMenu, setLilMenu] = useState({ isOpen: false, position: { top: 0, left: 0 } });
    const [tableMenu, setTableMenu] = useState({ isOpen: false, position: null });
    const [linkContext, setLinkContext] = useState(null);
    const lastClickedLink = useRef(0);
    const canEdit = true; // El editor colaborativo en tareas siempre intenta ser editable

    useEffect(() => () => {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    }, []);

    const latestOnSave = useRef(onSaveDescription);
    useEffect(() => {
        latestOnSave.current = onSaveDescription;
    }, [onSaveDescription]);

    const handleUpdate = useCallback((html) => {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => {
            if (latestOnSave.current) latestOnSave.current(html);
        }, 1200);
    }, []);

    const handleUpload = async (file, ed) => {
        await uploadAndInsertFile(ed, file, { userId, onProgress: setUploadProgress });
        if (latestOnSave.current) {
            latestOnSave.current(ed.getHTML());
        }
    };

    const handleEditorReady = useCallback((ed) => {
        setEditor(ed);
    }, []);

    // ── LilMenu (toolbar flotante en selección) ──
    const checkSelection = useCallback(() => {
        if (!editor || !canEdit) return;
        const sel = window.getSelection();
        if (!sel) { 
            setLilMenu((p) => (p.isOpen ? { ...p, isOpen: false } : p)); 
            setTableMenu((p) => (p.isOpen ? { ...p, isOpen: false } : p));
            return; 
        }
        const editorDom = editor.view?.dom;
        if (!editorDom) return;
        if (sel.anchorNode) {
            const el = sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement;
            if (el?.closest('.lilmenu-container') || el?.closest('.tablemenu-container')) return;
        }
        if (Date.now() - lastClickedLink.current < 200) return;

        const clampLeft = (rawLeft, scrollX) => {
            const half = 180; const margin = 10;
            let left = rawLeft;
            if (left - scrollX < half + margin) left = scrollX + half + margin;
            else if (left - scrollX + half > window.innerWidth - margin) left = scrollX + window.innerWidth - half - margin;
            return left;
        };

        let newLilMenu = { isOpen: false, position: { top: 0, left: 0 } };
        let newLinkContext = null;
        let hasSelection = false;

        if (sel.isCollapsed) {
            if (editorDom.contains(sel.anchorNode)) {
                let node = sel.anchorNode; let anchorEl = null;
                while (node && node !== editorDom) {
                    if (node.nodeType === 1 && node.tagName === 'A') { anchorEl = node; break; }
                    node = node.parentNode;
                }
                if (anchorEl) {
                    const rect = anchorEl.getBoundingClientRect();
                    let top = rect.top + window.scrollY - 20;
                    if (rect.top < 60) top = rect.bottom + window.scrollY + 54;
                    const left = clampLeft(rect.left + window.scrollX + rect.width / 2, window.scrollX);
                    newLinkContext = { href: anchorEl.href, text: anchorEl.textContent, color: rgbToHex(anchorEl.style.color) || '#4CAF50', element: anchorEl };
                    newLilMenu = { isOpen: true, position: { top, left } };
                }
            }
        } else if (editorDom.contains(sel.anchorNode) && sel.toString().trim().length > 0) {
            hasSelection = true;
            const rect = sel.getRangeAt(0).getBoundingClientRect();
            if (rect.width !== 0 || rect.height !== 0) {
                let top = rect.top + window.scrollY - 20;
                if (rect.top < 60) top = rect.bottom + window.scrollY + 54;
                const left = clampLeft(rect.left + window.scrollX + rect.width / 2, window.scrollX);
                newLilMenu = { isOpen: true, position: { top, left } };
            }
        }

        setLinkContext(newLinkContext);
        setLilMenu(newLilMenu);

        if (editor.isActive('table')) {
            if (newLilMenu.isOpen) {
                setTableMenu({ isOpen: true, position: { top: newLilMenu.position.top + 46, left: newLilMenu.position.left } });
            } else {
                let node = sel.anchorNode;
                let cellEl = null;
                while (node && node !== editorDom) {
                    if (node.nodeType === 1 && (node.tagName === 'TD' || node.tagName === 'TH')) { cellEl = node; break; }
                    node = node.parentNode;
                }
                if (cellEl) {
                    const rect = cellEl.getBoundingClientRect();
                    let top = rect.top + window.scrollY - 46;
                    if (rect.top < 60) top = rect.bottom + window.scrollY + 10;
                    const left = clampLeft(rect.left + window.scrollX + rect.width / 2, window.scrollX);
                    setTableMenu({ isOpen: true, position: { top, left } });
                } else {
                    setTableMenu({ isOpen: false, position: null });
                }
            }
        } else {
            setTableMenu({ isOpen: false, position: null });
        }
    }, [editor, canEdit]);

    useEffect(() => {
        document.addEventListener('selectionchange', checkSelection);
        return () => document.removeEventListener('selectionchange', checkSelection);
    }, [checkSelection]);

    const handleEditorClick = (e) => {
        const anchorEl = e.target.closest && e.target.closest('a');
        if (!canEdit) {
            if (anchorEl?.href && anchorEl.classList.contains('external')) window.open(anchorEl.href, '_blank', 'noopener,noreferrer');
            return;
        }
        if (anchorEl && editor) {
            e.preventDefault();
            lastClickedLink.current = Date.now();
            const rect = anchorEl.getBoundingClientRect();
            let top = rect.top + window.scrollY - 20;
            if (rect.top < 60) top = rect.bottom + window.scrollY + 54;
            const left = rect.left + window.scrollX + rect.width / 2;
            setLinkContext({ href: anchorEl.href, text: anchorEl.textContent, color: rgbToHex(anchorEl.style.color) || '#4CAF50', element: anchorEl });
            setLilMenu({ isOpen: true, position: { top, left } });
        }
    };

    const handleLilMenuSelect = (action, value) => {
        if (!editor || !canEdit) return;
        const c = () => editor.chain().focus();
        if (action === 'format') {
            const map = { bold: () => c().toggleBold().run(), italic: () => c().toggleItalic().run(), underline: () => c().toggleUnderline().run(), strikeThrough: () => c().toggleStrike().run(), quote: () => c().toggleBlockquote().run(), removeFormat: () => c().unsetAllMarks().clearNodes().run() };
            map[value]?.();
        } else if (action === 'text') {
            c().setParagraph().run();
        } else if (action === 'heading') {
            c().toggleHeading({ level: value }).run();
        } else if (action === 'size') {
            const lvl = { '25px': 1, '22px': 1, '18px': 2, '14px': 3 }[value];
            if (lvl) c().toggleHeading({ level: lvl }).run(); else c().setParagraph().run();
        } else if (action === 'list') {
            const map = { bullet: () => c().toggleBulletList().run(), numbered: () => c().toggleOrderedList().run(), checklist: () => c().toggleTaskList().run() };
            map[value]?.();
        } else if (action === 'insert-block') {
            if (value === 'code') c().toggleCodeBlock().run();
            else if (value === 'bullet') c().toggleBulletList().run();
            else if (value === 'numbered') c().toggleOrderedList().run();
            else if (value === 'columns') c().insertContent('<div class="doc-column-container"><div class="doc-column"><p>Columna 1</p></div><div class="doc-column"><p>Columna 2</p></div></div>').run();
            else if (value === 'toggle') c().insertContent('<details><summary>Lista contraíble</summary><p>Contenido...</p></details>').run();
            else if (value === 'inline-social-outline') c().insertContent({ type: 'inlineSocialOutline', attrs: { id: `social-outline-${Date.now()}`, outlineState: null } }).run();
        } else if (action === 'link') {
            if (value && typeof value === 'object') {
                const { url, text: linkText, color } = value;
                if (!url) return;
                if (linkContext?.element) {
                    linkContext.element.href = url;
                    if (color) {
                        const rgb = hexToRgbStr(color);
                        linkContext.element.style.color = color;
                        linkContext.element.style.background = `rgba(${rgb}, 0.15)`;
                        linkContext.element.style.border = `1px solid rgba(${rgb}, 0.2)`;
                    }
                    if (linkText) {
                        const span = linkContext.element.querySelector('.doc-tag-text');
                        if (span) span.textContent = linkText; else linkContext.element.textContent = linkText;
                    }
                    editor.commands.focus();
                } else {
                    const display = linkText || url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
                    let styleStr = '';
                    if (color) {
                        const rgb = hexToRgbStr(color);
                        styleStr = `style="color:${color};background:rgba(${rgb},0.15);border:1px solid rgba(${rgb},0.2);text-decoration:none;border-radius:4px;padding:1px 6px;font-weight:500;"`;
                    }
                    c().insertContent(`&nbsp;<a href="${url}" target="_blank" class="doc-tag external" ${styleStr}><span class="doc-tag-emoji">🔗</span> <span class="doc-tag-text">${display}</span></a>&nbsp;`).run();
                }
            } else if (value) {
                c().setLink({ href: value }).run();
            }
        } else if (action === 'convert') {
            if (value === 'tarea') c().toggleTaskList().run();
            else if (value === 'documento') c().setParagraph().run();
        }
        setLilMenu((p) => ({ ...p, isOpen: false }));
        setLinkContext(null);
    };

    const users = (usersToSelectFrom && usersToSelectFrom.length > 0) ? usersToSelectFrom : systemUsers;
    const usersRef = useRef(users);
    useEffect(() => {
        usersRef.current = (usersToSelectFrom && usersToSelectFrom.length > 0) ? usersToSelectFrom : systemUsers;
    }, [usersToSelectFrom, systemUsers]);

    const getUsers = useCallback(() => usersRef.current || [], []);

    const customExtensions = useMemo(() => [
        TaskCommentImage,
        TaskCommentVideo,
        TaskCommentFileAttachment,
        DatabaseExtension,
        DocTagExtension,
        TaskCommentMention.configure({
            suggestion: createTaskCommentSuggestion({
                getUsers,
            }),
        }),
        ColumnContainerExtension,
        ColumnExtension,
        InlineFrameExtension.configure({ currentUser }),
        Table.configure({ resizable: true }),
        TableRow,
        TableHeader,
        TableCell,
    ], [getUsers, currentUser]);

    const collabEditorInstance = useMemo(() => (
        <CollaborativeEditor
            key={taskId}
            documentId={`task-desc-${taskId}`}
            initialHtml={initialHtml}
            currentUser={currentUser}
            placeholder={translate('task.descriptionPlaceholder')}
            className="doctex-rich-editor task-panel-description-textarea"
            onUpdate={handleUpdate}
            onUpload={handleUpload}
            onEditorReady={handleEditorReady}
            onSelectionUpdate={checkSelection}
            customExtensions={customExtensions}
        />
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [taskId, initialHtml, currentUser, translate, customExtensions, handleEditorReady, handleUpdate]);

    const parentAssigneeNick = parentTask?.assignee ? parentTask.assignee.split(',')[0].trim() : null;
    const parentAssigneeUser = parentAssigneeNick ? systemUsers.find(u => u.nick === parentAssigneeNick) : null;

    return (
        <div onClick={handleEditorClick} style={{ position: 'relative' }}>
            {parentTask && (
                <div className="task-panel-parent-task-area" style={{ marginBottom: 'var(--space-4)' }}>
                    <div className="description-title-label" style={{ marginBottom: 'var(--space-2)' }}>Tarea padre</div>
                    <div
                        className="subtask-item"
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: 'var(--space-2) var(--space-3)', backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(255,255,255,0.05)', transition: 'background-color 0.2s' }}
                        onClick={() => onOpenTask(parentTask.id)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)'}
                    >
                        {parentAssigneeUser ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                <img src={(parentAssigneeUser['link-profile-img-thumb'] || parentAssigneeUser['link-profile-img']) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${parentAssigneeUser.nick}`} alt={parentAssigneeUser.nick} loading="lazy" style={{ width: '16px', height: '16px', borderRadius: '50%', objectFit: 'cover' }} />
                                <span style={{ fontSize: '11px', color: '#aaa', fontWeight: '500' }}>{parentAssigneeUser.nick}</span>
                            </div>
                        ) : (
                            <div style={{ width: '14px', height: '14px', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {parentTask.stage === 'done' ? <CheckCircle2 size={10} color="#10b981" /> : (parentTask.stage === 'check' ? <CheckCircle2 size={10} color="#eab308" /> : null)}
                            </div>
                        )}
                        <span style={{ fontSize: '13px', color: '#ccc', textDecoration: parentTask.stage === 'done' ? 'line-through' : 'none', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{parentTask.title || translate('common.untitled')}</span>
                    </div>
                </div>
            )}
            <div
                className="task-panel-description-area"
                style={{ marginTop: 'var(--space-2)', position: 'relative', pointerEvents: isDatePopoverOpen ? 'none' : undefined }}
            >
                {collabEditorInstance}
                {uploadProgress > 0 && (
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px', backgroundColor: 'transparent', borderRadius: '0 0 12px 12px', overflow: 'hidden', zIndex: 5 }}>
                        <div style={{ width: `${uploadProgress}%`, height: '100%', backgroundColor: '#34d399', transition: 'width 0.2s' }} />
                    </div>
                )}
            </div>
            
            <StickyTableHeadersOverlay editor={editor} />
            
            {lilMenu.isOpen && createPortal(
                <LilMenu
                    isOpen={lilMenu.isOpen}
                    position={lilMenu.position}
                    onSelect={handleLilMenuSelect}
                    onClose={() => { setLilMenu((p) => ({ ...p, isOpen: false })); setLinkContext(null); }}
                    linkContext={linkContext}
                />,
                document.body,
            )}
        </div>
    );
};

export default TaskDescriptionEditor;
